import { useState } from 'react';
import { Form, Input, Modal, Typography } from 'antd';
import { ApiError } from '@mall/shared';
import { useToast } from '../../app/ToastProvider';
import { changePassword, changeSecondPassword } from './api';

type PrimaryForm = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

type SecondForm = {
  currentSecondPassword: string;
  newPassword: string;
  confirmPassword: string;
};

type Kind = 'login' | 'second';

export function ChangePasswordModal({
  open,
  kind,
  onClose,
}: {
  open: boolean;
  kind: Kind;
  onClose: () => void;
}) {
  const toast = useToast();
  const [primaryForm] = Form.useForm<PrimaryForm>();
  const [secondForm] = Form.useForm<SecondForm>();
  const [loading, setLoading] = useState(false);

  const handleCancel = () => {
    primaryForm.resetFields();
    secondForm.resetFields();
    onClose();
  };

  const handleOk = async () => {
    if (kind === 'login') {
      try {
        const v = await primaryForm.validateFields();
        if (v.newPassword !== v.confirmPassword) {
          primaryForm.setFields([
            { name: 'confirmPassword', errors: ['两次输入的新密码不一致'] },
          ]);
          return;
        }
        setLoading(true);
        await changePassword({
          currentPassword: v.currentPassword,
          newPassword: v.newPassword,
        });
        toast.success('登录密码已更新');
        primaryForm.resetFields();
        onClose();
      } catch (err) {
        if (err && typeof err === 'object' && 'errorFields' in err) {
          return;
        }
        toast.error(err instanceof ApiError ? err.message : '修改失败');
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const v = await secondForm.validateFields();
      if (v.newPassword !== v.confirmPassword) {
        secondForm.setFields([
          { name: 'confirmPassword', errors: ['两次输入的新密码不一致'] },
        ]);
        return;
      }
      setLoading(true);
      await changeSecondPassword({
        currentSecondPassword: v.currentSecondPassword?.trim() || undefined,
        newSecondPassword: v.newPassword,
      });
      toast.success('二次密码已更新');
      secondForm.resetFields();
      onClose();
    } catch (err) {
      if (err && typeof err === 'object' && 'errorFields' in err) {
        return;
      }
      toast.error(err instanceof ApiError ? err.message : '修改失败');
    } finally {
      setLoading(false);
    }
  };

  const title = kind === 'login' ? '修改登录密码' : '修改二次密码';

  return (
    <Modal
      title={title}
      open={open}
      onCancel={handleCancel}
      onOk={() => void handleOk()}
      confirmLoading={loading}
      destroyOnClose
    >
      {kind === 'login' ? (
        <Form form={primaryForm} layout="vertical" preserve={false} autoComplete="off">
          <Form.Item
            name="currentPassword"
            label="当前登录密码"
            rules={[{ required: true, message: '请输入当前密码' }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 8, message: '至少 8 个字符' },
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="确认新密码"
            rules={[{ required: true, message: '请再次输入新密码' }]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      ) : (
        <Form form={secondForm} layout="vertical" preserve={false} autoComplete="off">
          <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
            若从未设置过二次密码，可留空「当前二次密码」仅填写新密码；已设置则需填写当前值。
          </Typography.Paragraph>
          <Form.Item name="currentSecondPassword" label="当前二次密码（可选）">
            <Input.Password placeholder="首次设置可留空" />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="新二次密码"
            rules={[
              { required: true, message: '请输入新二次密码' },
              { min: 8, message: '至少 8 个字符' },
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="确认新二次密码"
            rules={[{ required: true, message: '请再次输入' }]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
}
